/**
 * Tab Notification Manager
 * 
 * Manages browser tab notifications:
 * - Shows notification count in tab title
 * - Flashes title until user focuses window
 * - Can show badge on favicon
 */

class TabNotificationManager {
  constructor() {
    this.originalTitle = document.title;
    this.notificationCount = 0;
    this.isFlashing = false;
    this.flashInterval = null;
    this.showingNotification = false;
    this.originalFavicon = null;
    
    // Store original favicon
    const favicon = document.querySelector("link[rel*='icon']");
    if (favicon) {
      this.originalFavicon = favicon.href;
    }
    
    // Listen for window focus
    window.addEventListener('focus', this.handleFocus.bind(this));
    window.addEventListener('blur', this.handleBlur.bind(this));
    
    // Track if window is focused
    this.windowFocused = document.hasFocus();
  }
  
  handleFocus() {
    this.windowFocused = true;
    // Stop flashing when user focuses window
    this.stopFlashing();
    // Reset notification count when focused
    this.clearNotifications();
  }
  
  handleBlur() {
    this.windowFocused = false;
  }
  
  /**
   * Add a notification - increments count and starts flashing if window not focused
   */
  addNotification(message = null) {
    this.notificationCount++;
    this.updateTitle();
    
    // Start flashing if window is not focused
    if (!this.windowFocused && !this.isFlashing) {
      this.startFlashing(message);
    }
    
    // Try to show browser notification if permitted
    this.showBrowserNotification(message);
    
    // Update favicon with badge
    this.updateFaviconBadge();
  }
  
  /**
   * Set specific notification count
   */
  setNotificationCount(count) {
    this.notificationCount = count;
    this.updateTitle();
    
    if (count > 0 && !this.windowFocused && !this.isFlashing) {
      this.startFlashing();
    }
    
    if (count > 0) {
      this.updateFaviconBadge();
    } else {
      this.resetFavicon();
    }
  }
  
  /**
   * Clear all notifications
   */
  clearNotifications() {
    this.notificationCount = 0;
    this.updateTitle();
    this.stopFlashing();
    this.resetFavicon();
  }
  
  /**
   * Update the document title with notification count
   */
  updateTitle() {
    if (this.notificationCount > 0) {
      document.title = `(${this.notificationCount}) ${this.originalTitle}`;
    } else {
      document.title = this.originalTitle;
    }
  }
  
  /**
   * Start flashing the title
   */
  startFlashing(message = null) {
    if (this.isFlashing) return;
    
    this.isFlashing = true;
    const flashMessage = message || "🔔 Nouvelle notification!";
    let showOriginal = false;
    
    this.flashInterval = setInterval(() => {
      if (showOriginal) {
        document.title = `(${this.notificationCount}) ${this.originalTitle}`;
      } else {
        document.title = `⚡ ${flashMessage}`;
      }
      showOriginal = !showOriginal;
    }, 1000); // Flash every second
  }
  
  /**
   * Stop flashing the title
   */
  stopFlashing() {
    if (this.flashInterval) {
      clearInterval(this.flashInterval);
      this.flashInterval = null;
    }
    this.isFlashing = false;
    this.updateTitle();
  }
  
  /**
   * Update favicon to show notification badge
   */
  updateFaviconBadge() {
    if (this.notificationCount === 0) {
      this.resetFavicon();
      return;
    }
    
    // Create canvas for favicon with badge
    const canvas = document.createElement('canvas');
    canvas.width = 32;
    canvas.height = 32;
    const ctx = canvas.getContext('2d');
    
    // Load original favicon
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      // Draw original favicon
      ctx.drawImage(img, 0, 0, 32, 32);
      
      // Draw red badge circle
      ctx.beginPath();
      ctx.arc(24, 8, 10, 0, 2 * Math.PI);
      ctx.fillStyle = '#ef4444';
      ctx.fill();
      
      // Draw notification count
      ctx.fillStyle = 'white';
      ctx.font = 'bold 12px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const displayCount = this.notificationCount > 9 ? '9+' : this.notificationCount.toString();
      ctx.fillText(displayCount, 24, 8);
      
      // Update favicon
      const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
      link.rel = 'icon';
      link.href = canvas.toDataURL('image/png');
      document.head.appendChild(link);
    };
    
    img.onerror = () => {
      // If favicon fails to load, just draw badge
      ctx.beginPath();
      ctx.arc(16, 16, 14, 0, 2 * Math.PI);
      ctx.fillStyle = '#22c55e';
      ctx.fill();
      
      ctx.beginPath();
      ctx.arc(26, 6, 8, 0, 2 * Math.PI);
      ctx.fillStyle = '#ef4444';
      ctx.fill();
      
      ctx.fillStyle = 'white';
      ctx.font = 'bold 10px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const displayCount = this.notificationCount > 9 ? '9+' : this.notificationCount.toString();
      ctx.fillText(displayCount, 26, 6);
      
      const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
      link.rel = 'icon';
      link.href = canvas.toDataURL('image/png');
      document.head.appendChild(link);
    };
    
    img.src = this.originalFavicon || '/favicon.ico';
  }
  
  /**
   * Reset favicon to original
   */
  resetFavicon() {
    if (this.originalFavicon) {
      const link = document.querySelector("link[rel*='icon']");
      if (link) {
        link.href = this.originalFavicon;
      }
    }
  }
  
  /**
   * Show browser notification (requires permission)
   */
  async showBrowserNotification(message) {
    if (!('Notification' in window)) return;
    
    if (Notification.permission === 'default') {
      await Notification.requestPermission();
    }
    
    if (Notification.permission === 'granted' && !this.windowFocused) {
      const notification = new Notification('Middleman - Nouvelle notification', {
        body: message || 'Vous avez une nouvelle notification',
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag: 'middleman-notification',
        renotify: true
      });
      
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
      
      // Auto close after 5 seconds
      setTimeout(() => notification.close(), 5000);
    }
  }
  
  /**
   * Request notification permission
   */
  async requestPermission() {
    if ('Notification' in window && Notification.permission === 'default') {
      return await Notification.requestPermission();
    }
    return Notification.permission;
  }
}

// Create singleton instance
const tabNotificationManager = new TabNotificationManager();

export default tabNotificationManager;
